import React, { useState, useEffect } from 'react';
import LiveChatWidget from './LiveChatWidget';
import MissedCallCalculator from './MissedCallCalculator';
import axios from 'axios';

import AboutUs from './pages/AboutUs';
import FAQPage from './pages/FAQPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsPage from './pages/TermsPage';

export default function MyAIAgentSite() {
  const [route, setRoute] = useState(window.location.hash.replace('#', '') || 'home');
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash.replace('#', '') || 'home');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    // JSON-LD structured data for Organization + Service
    const ld = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "My AI Agent",
      "url": "https://myaiagent.biz",
      "logo": "/ai_logo.png",
      "sameAs": [],
      "contactPoint": [{
        "@type": "ContactPoint",
        "telephone": "+1-312-768-8270",
        "contactType": "Customer Service",
        "areaServed": "US"
      }]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  function navigate(target) {
    window.location.hash = target;
    setRoute(target);
  }

  async function submitLead(e) {
    e.preventDefault();
    const form = e.target;
    const data = {
      name: form.name.value,
      phone: form.phone.value,
      email: form.email.value,
      business: form.business.value,
      message: form.message.value,
      utm: window.location.search || ''
    };
    // TODO: replace with real endpoint. For now simulate success.

    await axios.post('https://api.myaiagent.biz/leads/aiagent', data)
    .then( res => {
      console.log('Success -> ', res.data)
    })
    .catch( err => {
      console.log('Some error here -> ', err)
    })

    console.log('Lead submitted', data);
    setFormStatus('success');
    form.reset();
    setTimeout(() => setFormStatus(null), 5000);
  }

  const Nav = () => (
    <nav className="bg-white/80 backdrop-blur sticky top-0 z-40 border-b">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <a href="#home"><img src="/ai_logo.png" alt="My AI Agent" className="w-25 h-25 rounded-md" /></a>
          <div>
            <a href="#home" className="text-lg font-bold text-slate-900">My AI Agent</a>
            <div className="text-xs text-slate-500">Missed Call Rescue • AI Lead Qualification</div>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-4">
          <a href="#home" className="hover:text-sky-600">Home</a>
          <a href="#about" className="hover:text-sky-600">About</a>
          <a href="#features" className="hover:text-sky-600">Features</a>
          <a href="#pricing" className="hover:text-sky-600">Pricing</a>
          <a href="#faq" className="hover:text-sky-600">FAQ</a>
          <a href="#contact" className="px-3 py-2 bg-sky-600 text-white rounded-md">Get Started</a>
          <a href="#calc" className="px-3 py-2 bg-red-600 text-white rounded-md">Calculator</a>
        </div>
        <div className="md:hidden">
          <select onChange={(e) => navigate(e.target.value)} className="border px-2 py-1 rounded">
            <option value="home">Home</option>
            <option value="about">About</option>
            <option value="features">Features</option>
            <option value="pricing">Pricing</option>
            <option value="faq">FAQ</option>
            <option value="contact">Contact</option>
          </select>
        </div>
      </div>
    </nav>
  );

  const Footer = () => (
    <footer className="bg-slate-900 text-slate-200 py-12">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
        <div>
          <h3 className="font-bold text-white">My AI Agent</h3>
          <p className="text-sm mt-2">Turn missed calls into booked appointments — automatically. © {new Date().getFullYear()} My AI Agent</p>

          <p className="text-sm mt-2">Messaging services are subject to TCPA and A2P 10DLC carrier regulations. All users must obtain proper consent, provide opt-out options, and comply with federal and carrier guidelines. My AI Agent is not responsible for misuse of the platform.</p>
        </div>
        <div>
          <h4 className="font-semibold">Quick Links</h4>
          <ul className="mt-2 text-sm space-y-1">
            <li><a href="#privacy">Privacy Policy</a></li>
            <li><a href="#terms">Terms & Conditions</a></li>
            <li><a href="#contact">Contact</a></li>
            <li><a href="#calc">Lost Revenue Calculator</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold">Contact</h4>
          <p className="text-sm mt-2">Email: <a href="mailto:myaiagentbiz@gmail.com" className="underline">myaiagentbiz@gmail.com</a></p>
          <p className="text-sm">Phone: +1-312-768-8270</p>
        </div>
      </div>
    </footer>
  );

  const Home = () => (
    <main>
      <section className="bg-gradient-to-br from-sky-50 to-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Never Lose Another Lead 🚀</h1>
            <h3 className="text-2xl font-bold mt-6">Instant Missed-Call Text-Back + AI Lead Response for Small Businesses</h3>
            <p className="mt-4 text-lg text-slate-700">When customers call and you can’t answer, My AI Agent responds instantly—automatically texting back, answering questions, collecting details, qualifies leads with AI, and can even book appointments — 24/7. Perfect for local service businesses who rely on phone leads.</p>
            <h3 className="text-lg font-bold mt-6">More leads. More appointments. More revenue. Zero effort.</h3>
            <div className="mt-6 flex items-center gap-3">
              <a href="#contact" className="px-5 py-3 bg-sky-600 text-white rounded-md font-semibold">Get Started</a>
              <a href="#pricing" className="px-4 py-3 border rounded-md">See Pricing</a>
            </div>
            <div className="mt-6 text-sm text-slate-500">Integrates with Google Calendar, Calendly, HubSpot, Microsoft Office and more.</div>
            <div className="mt-6">
              <h3 className="text-lg font-bold mt-6 mb-6">Your AI agent works 24/7 so you don’t have to.</h3>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <strong>Works for:</strong> Home services (plumbing, HVAC, roofing, landscaping), Real estate agents & brokers, Law firms & legal practices, Medical offices & med spas, Auto repair & dealerships, Cleaning & restoration companies
, Health & wellness (gyms, massage, chiropractors)
                {/* <strong>Works for:</strong> Plumbers, Contractors, Dentists, Real Estate, Law Firms, HVAC, Pest Control */}
              </div>
            </div>
          </div>
          <div>
            <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=60" alt="AI agent" className="w-full rounded-lg shadow-lg" />
          </div>
        </div>
      </section>

      <section id="features" className="max-w-6xl mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">

            <div>
                <img src="/howitworks.png" alt="happy customer" className="w-full rounded-lg shadow" />
            </div>

          <div><h2 className="text-3xl font-bold">How it Works ⚙️</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">1. Connect Your Number</h3>
            <p className="mt-2 text-sm text-slate-600">Use your existing phone or a new one. Setup takes minutes.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">2. Missed Calls? No Problem</h3>
            <p className="mt-2 text-sm text-slate-600">Whenever a call is missed, your AI agent sends an instant, friendly text back to the caller to re-engage and capture intent.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">3. AI Handles Conversations</h3>
            <p className="mt-2 text-sm text-slate-600">The agent answers questions, qualifies leads, collects details, filters spam, and books appointments automatically right into your calendar.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">4. Stay in Control</h3>
            <p className="mt-2 text-sm text-slate-600">View conversations in your dashboard, step in anytime, and track leads, bookings, and revenue.</p>
          </div>
        </div>
        <h3 className="text-lg font-bold mt-6 mb-6">Turn missed calls into customers—automatically.</h3>
        </div>

      


        </div>
        {/* <h2 className="text-3xl font-bold">How it Works ⚙️</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">1. Connect Your Number</h3>
            <p className="mt-2 text-sm text-slate-600">Use your existing phone or a new one. Setup takes minutes.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">2. Missed Calls? No Problem</h3>
            <p className="mt-2 text-sm text-slate-600">Whenever a call is missed, your AI agent sends an instant, friendly text back to the caller to re-engage and capture intent.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">3. AI Handles Conversations</h3>
            <p className="mt-2 text-sm text-slate-600">The agent answers questions, qualifies leads, collects details, filters spam, and books appointments automatically right into your calendar.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold">4. Stay in Control</h3>
            <p className="mt-2 text-sm text-slate-600">View conversations in your dashboard, step in anytime, and track leads, bookings, and revenue.</p>
          </div>
        </div>
        <h3 className="text-lg font-bold mt-6 mb-6">Turn missed calls into customers—automatically.</h3> */}
      </section>

      <section className="bg-slate-50 py-12">
        
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center"> 
          <div>
            <h3 className="text-2xl font-bold">The Problem 🤯</h3>
            <h3 className="text-lg font-bold mt-2">Missed calls = missed money.</h3>
            <ul className="mt-4 space-y-3 text-slate-700 mb-10">
              <li>📌 62% of calls to small businesses go unanswered</li>
              <li>📌 72% of customers won’t leave a voicemail</li>
              <li>📌 Most callers contact your competitor within 5 minutes</li>
              <li>📌 Missed calls cost businesses thousands every month</li>
            </ul>
            <h3 className="text-2xl font-bold">Why businesses love it ❤️</h3>
            <ul className="mt-4 space-y-3 text-slate-700">
              <li>⚡ Recover 20–40% of lost leads automatically</li>
              <li>💵 Immediate ROI(Return on Investment)</li>
              <li>🧠 Smart messaging prompts customers to reply</li>
              <li>🗓️ More appointments booked with less effort</li>
              <li>⏰ Works 24/7/365</li>
              <li>🔌 Connects to any CRM, calendar, or workflow</li>
              <li>🤖 Fully automated once set up</li>
              <li>💻 Requires no tech knowledge</li>
            </ul>
          </div>
          <div>
            <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=900&q=60" alt="happy customer" className="w-full rounded-lg shadow" />
          </div>
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold">Pricing 💳</h2>
        <h3 className="text-2xl font-semibold mt-6 text-center ">How much does a missed lead cost you?</h3>
        <div className="mt-6 grid md:grid-cols-3 gap-6">

          <div className="border rounded-lg p-6 text-center">
            <h3 className="font-semibold">Starter</h3>
            <div className="text-3xl font-bold mt-4">$49<span className="text-sm">/mo</span></div>
            <p className="text-base font-semibold mt-2 mb-2">Perfect for solo operators and small businesses who want to stop losing leads.</p>
            
            <ul className="mt-6 text-sm text-slate-600 space-y-2">
              <li className="font-bold underline">Includes:</li>
              <li>Automatic text-back for missed calls</li>
              <li>AI Appointment scheduling + calendar sync</li>
              <li>Customizable autoresponder</li>
              <li>Basic AI conversation handling</li>
              <li>Basic lead logging</li>
              <li>Email notifications</li>
              <li>Basic CRM integrations</li>
              <li>Email & chat support</li>
            </ul>
            <p className="text-base font-semibold mt-2 mb-2">Best for: Individual contractors, solo field service pros, small teams.</p>
            <a href="#contact"><button className="mt-6 px-6 py-2 bg-sky-600 text-white rounded">Book a Demo</button></a>
          </div>
          <div className="border rounded-lg p-6 text-center border-sky-500 outline-2">
            <h3 className="font-semibold">Pro</h3>
            <div className="text-3xl font-bold mt-4">$149<span className="text-sm">/mo</span></div>
            <p className="text-base font-semibold mt-2 mb-2">Our most popular plan — AI that books more jobs while you work.</p>
            <ul className="mt-6 text-sm text-slate-600 space-y-2">
              <li className="font-bold underline">Includes everything in Starter, plus:</li>
              <li>Smart AI lead qualification</li>
              <li>Automated text follow ups for appointment reminders, confirmations, and cancellations w/ calendar sync</li>
              <li>Multi-step workflows & follow-up sequences</li>
              <li>My AI Social Agent - AI-agent to reply to social media messages (Facebook Messenger, X/Threads, Instagram DMs)</li>
              <li>AI Live Chat Widget for Website</li>
              <li>AI Appointment scheduling lead form for website w/ calendar sync</li>
              <li>Slack & CRM integration (HubSpot, GHL, Salesforce, etc.)</li>
              <li>Multi-user support</li>
              <li>Priority support</li>
            </ul>
            <p className="text-base font-semibold mt-2 mb-2">Best for: Growing service companies, real estate teams, clinics, automotive, and high-volume inbound businesses.</p>
            <a href="#contact"><button className="mt-6 px-6 py-2 bg-sky-600 text-white rounded">Book a Demo</button></a>
          </div>
          <div className="border rounded-lg p-6 text-center">
            <h3 className="font-semibold">Elite</h3>
            <div className="text-3xl font-bold mt-4">$299<span className="text-sm">/mo</span></div>
            <p className="text-base font-semibold mt-2 mb-2">Full-featured automation for businesses that need serious lead capture power.</p>
            <ul className="mt-6 text-sm text-slate-600 space-y-2">
              <li className="font-bold underline">Includes everything in Pro, plus:</li>
              <li>Full workflow customization</li>
              <li>Dedicated engineer (hourly)</li>
              <li>Priority support</li>
              <li>Voice AI call answering (Coming soon...)</li>
            </ul>
            <p className="text-base font-semibold mt-2 mb-2">Best for: Company or business that needsa truly customized AI backed automation solution.</p>
            <a href="#contact"><button className="mt-6 px-6 py-2 bg-sky-600 text-white rounded">Book a Demo</button></a>
          </div>
        </div>
        <h3 className="text-2xl font-semibold mt-6 text-center ">Recovering just <span className='font-bold'>ONE</span> lost lead per week could cover your subscription</h3>
       
       <div class="flex justify-center mt-12">
          <a href="#calc" className="px-5 py-3 bg-sky-600 text-white rounded-md font-semibold">Lost Revenue Calculator</a>
       </div>
        
      </section>

      <section id="contact" className="bg-white py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold">Get a free demo 🤝</h2>
            <p className="mt-2 text-slate-600">Fill out the form and we'll reach out within one business day to set up a short demo.</p>
            <form onSubmit={submitLead} className="mt-4 space-y-3 bg-slate-50 p-4 rounded">
              <div className="grid md:grid-cols-2 gap-3">
                <input name="name" required placeholder="Your name" className="p-2 border rounded" />
                <input name="business" placeholder="Business (optional)" className="p-2 border rounded" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <input name="phone" required placeholder="Phone" className="p-2 border rounded" />
                <input name="email" required placeholder="Email" className="p-2 border rounded" />
              </div>
              <textarea name="message" placeholder="Tell us about your needs" className="w-full p-2 border rounded" rows={4}></textarea>
              <div className="flex items-center justify-between">
                <button type="submit" className="px-4 py-2 bg-sky-600 text-white rounded">Request Demo</button>
                {formStatus === 'success' && <div className="text-green-600">Thanks — we'll be in touch!</div>}
              </div>
            </form>
          </div>
          <div>
            <h3 className="text-2xl font-bold">Contact Info</h3>
            <p className="mt-2">Email: <a href="mailto:myaiagentbiz@gmail.com" className="underline">myaiagentbiz@gmail.com</a></p>
            <p>Phone: +1-312-768-8270</p>
            <div className="mt-6">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=60" alt="support" className="w-full rounded shadow" />
            </div>
          </div>
        </div>
      </section>

    </main>
  );

  const About = () => (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <h2 className="text-3xl font-bold">About My AI Agent</h2>
      <p className="mt-4 text-slate-700">My AI Agent was built for small and local service businesses that cannot afford to miss phone leads. We combine reliable telephony (Telnyx), workflow automation (n8n), and modern AI to make sure every missed call becomes an opportunity.</p>
      <h3 className="mt-6 font-semibold">Our Mission</h3>
      <p className="mt-2 text-slate-600">To help small businesses recover lost revenue by automating follow-up conversations and turning callers into paying customers.</p>
      <h3 className="mt-6 font-semibold">Team</h3>
      <div className="mt-4 grid md:grid-cols-3 gap-6">
        <div className="p-4 bg-white rounded shadow">
          <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=60" className="w-24 h-24 rounded-full mx-auto" alt="founder" />
          <h4 className="text-center mt-3 font-semibold">Andrew Burton</h4>
          <div className="text-sm text-slate-600 text-center">Founder • Full-stack & AI</div>
        </div>
        <div className="p-4 bg-white rounded shadow">
          <img src="https://images.unsplash.com/photo-1545996124-1b42f6d6d1d9?auto=format&fit=crop&w=400&q=60" className="w-24 h-24 rounded-full mx-auto" alt="engineer" />
          <h4 className="text-center mt-3 font-semibold">Engineering</h4>
          <div className="text-sm text-slate-600 text-center">DevOps, n8n, Integrations</div>
        </div>
        <div className="p-4 bg-white rounded shadow">
          <img src="https://images.unsplash.com/photo-1544006659-f0b21884ce1d?auto=format&fit=crop&w=400&q=60" className="w-24 h-24 rounded-full mx-auto" alt="support" />
          <h4 className="text-center mt-3 font-semibold">Support</h4>
          <div className="text-sm text-slate-600 text-center">Onboarding & Customer Success</div>
        </div>
      </div>
    </div>
  );

  const FAQ = () => (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <h2 className="text-3xl font-bold">FAQ ❓</h2>
      <div className="mt-6 space-y-4">
        <div>
          <h4 className="font-semibold">How quickly does the SMS go out after a missed call?</h4>
          <p className="text-slate-600">Typically within 5–10 seconds. We include a short delay to avoid false positives.</p>
        </div>
        <div>
          <h4 className="font-semibold">Do you use my phone number to send texts?</h4>
          <p className="text-slate-600">We use a messaging-enabled Telnyx number associated with your account. This can be your existing number if ported or a new number we provide.</p>
        </div>
        <div>
          <h4 className="font-semibold">Is the AI secure and compliant?</h4>
          <p className="text-slate-600">We follow best practices for data handling, and you can choose whether transcripts are stored. We also support DNC/STOP handling and 10DLC registration where needed.</p>
        </div>
      </div>
    </div>
  );

  const Privacy = () => (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h2 className="text-3xl font-bold">Privacy Policy</h2>
      <p className="mt-4 text-slate-700">At My AI Agent (myaiagent.biz) we respect your privacy. This page explains what information we collect and how we use it.</p>
      <h3 className="mt-6 font-semibold">Information We Collect</h3>
      <p className="text-slate-600">We may collect contact information, phone numbers, SMS content, and scheduling details to provide the service. We do not sell your personal data.</p>
      <h3 className="mt-6 font-semibold">How We Use Data</h3>
      <p className="text-slate-600">Data is used to send SMS, to qualify leads, and to create calendar events. You can request deletion of your data by contacting myaiagentbiz@gmail.com.</p>
    </div>
  );

  const Terms = () => (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h2 className="text-3xl font-bold">Terms & Conditions</h2>
      <p className="mt-4 text-slate-700">By using My AI Agent you agree to our terms. Our service automates communication and scheduling on your behalf. You are responsible for following local laws and obtaining necessary consent for contacting customers.</p>
    </div>
  );

  const NotFound = () => (
    <div className="max-w-4xl mx-auto px-6 py-24 text-center">
      <h2 className="text-4xl font-bold">404 — Page not found</h2>
      <p className="mt-4 text-slate-600">Sorry, we couldn't find that page. Try returning <a href="#home" className="underline">home</a>.</p>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col font-sans bg-white text-slate-800">
      <Nav />
      <div className="flex-1">
        {route === 'home' && <Home />}
        {route === 'about' && <AboutUs />}
        {route === 'features' && <Home />}
        {route === 'pricing' && <Home />}
        {route === 'contact' && <Home />}
        {route === 'faq' && <FAQPage />}
        {route === 'privacy' && <PrivacyPolicy />}
        {route === 'terms' && <TermsPage />}
        {route === '404' && <NotFound />}
        {route === 'calc' && <MissedCallCalculator />}
      </div>
      {/* <LiveChatWidget /> */}
      <Footer />
    </div>
  );
}
